/**
 * Patient360 — Financials Tab (Reimbursement Operations Center)
 *
 * Layout:
 * - Compact KPI strip (4 metrics, single row)
 * - 2-column grid: Left (ledger + claims) / Right (scheme summary + eligibility)
 * - Operational empty state with actionable CTAs
 * - Financial progress visualization
 * - Transaction table always present (even if empty)
 */

import { memo, useMemo } from 'react';
import {
  CheckCircle,
  Clock,
  DollarSign,
  Download,
  Receipt,
  ReceiptText,
  TrendingUp,
  Wallet,
  XCircle,
} from 'lucide-react';
import { colorSystem } from '@/design-system/colorSystem';
import { OpsKpiStrip, type OpsKpiItem } from '@/design-system/workspace';
import { Button } from '@/modules/shared/components/ui/button';
import { DataGrid, type DataGridColumn } from '@/modules/shared/components/ui/table/DataGrid';
import { StatusBadge } from '@/modules/shared/components/ui/StatusBadge';
import { formatCurrency } from '@/modules/shared/utils/formatCurrency';
import { formatDisplayDate } from '../../../utils/formatUtils';
import { formatWorkflowAction } from '@/modules/shared/utils/formatWorkflowAction';
import type {
  Patient360FinancialDto,
  Patient360ClaimDto,
  Patient360SchemeBreakdownDto,
} from '../../../types/patient360';

// ─── Types ─────────────────────────────────────────────────────

interface FinancialTabSummary {
  totalAssistance: number;
  remainingEligibility: number;
  schemeBreakdown: Patient360SchemeBreakdownDto[];
}

interface FinancialsTabProps {
  financial: Patient360FinancialDto;
  claims: Patient360ClaimDto[];
  summary: FinancialTabSummary;
}

// ─── Utility ───────────────────────────────────────────────────

function getClaimStatusVariant(status: string): 'success' | 'warning' | 'muted' | 'error' | 'info' {
  const normalized = status?.toUpperCase() || '';
  if (['APPROVED', 'PAID', 'COMPLETED'].includes(normalized)) return 'success';
  if (['PENDING', 'REVIEW', 'SUBMITTED'].includes(normalized)) return 'warning';
  if (['REJECTED', 'DENIED'].includes(normalized)) return 'error';
  if (['IN_REVIEW', 'PROCESSING'].includes(normalized)) return 'info';
  return 'muted';
}

// ─── Sub-Components ────────────────────────────────────────────

/** Scheme/Plan Summary Card — feature card */
function SchemeSummaryPanel({ schemes }: { schemes: Patient360SchemeBreakdownDto[] }) {
  if (!schemes || schemes.length === 0) return null;

  return (
    <div className="space-y-2.5">
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Assistance Plans</p>
      {schemes.map((scheme, idx) => {
        const usagePercent = scheme.totalCap && scheme.totalCap > 0
          ? Math.min(100, Math.round((scheme.totalUsed / scheme.totalCap) * 100))
          : null;

        return (
          <div key={idx} className="rounded-xl border border-border/50 bg-[#F8FAFC] dark:bg-muted/10 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_12px_rgba(15,23,42,0.04)] space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[13px] font-bold text-foreground truncate">{scheme.schemeName}</p>
              {scheme.planType && <StatusBadge status={scheme.planType} className="text-[11px]" />}
            </div>

            {/* Financial breakdown */}
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center rounded-lg bg-white dark:bg-background border border-border/40 py-2 px-1.5">
                <p className="text-[12px] font-bold text-foreground">{scheme.caseCount}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Cases</p>
              </div>
              <div className="text-center rounded-lg bg-white dark:bg-background border border-border/40 py-2 px-1.5">
                <p className="text-[12px] font-bold text-foreground">{formatCurrency(scheme.totalUsed)}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Used</p>
              </div>
              <div className="text-center rounded-lg bg-white dark:bg-background border border-border/40 py-2 px-1.5">
                <p className="text-[12px] font-bold text-primary">{formatCurrency(scheme.remainingCap ?? 0)}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Remaining</p>
              </div>
            </div>

            {/* Progress bar */}
            {usagePercent !== null && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-muted-foreground">Cap Utilization</span>
                  <span className="text-[11px] font-bold" style={{ color: usagePercent > 90 ? colorSystem.danger.dark : usagePercent > 70 ? colorSystem.warning.dark : colorSystem.primary.dark }}>{usagePercent}%</span>
                </div>
                <div className="h-2 w-full bg-muted/60 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${usagePercent}%`,
                      background: usagePercent > 90 ? colorSystem.danger.dark : usagePercent > 70 ? colorSystem.warning.dark : colorSystem.primary.dark,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Eligibility overview panel — premium card */
function EligibilityPanel({ summary }: { summary: FinancialTabSummary }) {
  const total = summary.totalAssistance + summary.remainingEligibility;
  const usedPercent = total > 0 ? Math.round((summary.totalAssistance / total) * 100) : 0;

  if (total === 0) return null;

  return (
    <div className="rounded-xl border border-border/50 bg-[#F8FAFC] dark:bg-muted/10 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_12px_rgba(15,23,42,0.04)] space-y-3">
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Eligibility Overview</p>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">Utilized</span>
        <span className="text-[11px] font-bold" style={{ color: colorSystem.primary.dark }}>{usedPercent}%</span>
      </div>
      <div className="h-2 w-full bg-muted/60 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${usedPercent}%`, backgroundColor: colorSystem.primary.dark }} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="text-center rounded-lg bg-white dark:bg-background border border-border/40 py-2 px-1.5">
          <p className="text-[13px] font-bold text-foreground">{formatCurrency(summary.totalAssistance)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Used</p>
        </div>
        <div className="text-center rounded-lg bg-white dark:bg-background border border-border/40 py-2 px-1.5">
          <p className="text-[13px] font-bold text-foreground">{formatCurrency(summary.remainingEligibility)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Available</p>
        </div>
      </div>
    </div>
  );
}

/** Pending actions summary */
function PendingActionsPanel({ claims }: { claims: Patient360ClaimDto[] }) {
  const pending = claims.filter(c => ['PENDING', 'REVIEW', 'SUBMITTED'].includes(c.claimStatus?.toUpperCase() || ''));
  if (pending.length === 0) return null;

  return (
    <div className="p360-alert-banner space-y-1.5" style={{ borderColor: 'hsl(var(--kpi-orange-border))', background: 'linear-gradient(90deg, hsl(var(--surface-warning-soft)) 0%, hsl(var(--surface-card)) 100%)' }}>
      <div className="flex items-center gap-1.5">
        <Clock className="h-3 w-3 text-warning" />
        <p className="text-[11px] font-semibold text-foreground">Pending Actions ({pending.length})</p>
      </div>
      <div className="space-y-1">
        {pending.slice(0, 3).map(claim => (
          <div key={claim.claimId} className="flex items-center justify-between text-[11px]">
            <span className="text-foreground font-medium truncate">{claim.claimNumber || 'Claim'}</span>
            <span className="text-muted-foreground shrink-0">{formatCurrency(claim.claimedAmount || 0)}</span>
          </div>
        ))}
        {pending.length > 3 && (
          <p className="text-[11px] text-muted-foreground">+{pending.length - 3} more</p>
        )}
      </div>
    </div>
  );
}

/** Empty state — premium with CTA */
function FinancialEmptyState() {
  return (
    <div className="rounded-xl border border-border/50 bg-[#F8FAFC] dark:bg-muted/10 p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col items-center text-center max-w-sm mx-auto">
        <div className="rounded-xl bg-muted/40 p-3 mb-3">
          <Receipt className="h-5 w-5 text-muted-foreground" />
        </div>
        <h4 className="text-[14px] font-bold text-foreground">No financial transactions yet</h4>
        <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
          Transactions, claims, reimbursements, and utilization history will appear here once financial activity begins.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground w-full">
          <div className="flex items-center gap-1.5 rounded-md bg-white dark:bg-background border border-border/40 px-2 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorSystem.primary.dark }} />
            Claims submitted
          </div>
          <div className="flex items-center gap-1.5 rounded-md bg-white dark:bg-background border border-border/40 px-2 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorSystem.success.dark }} />
            Utilization recorded
          </div>
          <div className="flex items-center gap-1.5 rounded-md bg-white dark:bg-background border border-border/40 px-2 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorSystem.warning.dark }} />
            Invoice validated
          </div>
          <div className="flex items-center gap-1.5 rounded-md bg-white dark:bg-background border border-border/40 px-2 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorSystem.info.dark }} />
            Assistance approved
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────

/** Claim Status Breakdown Strip — mini analytics */
function ClaimStatusStrip({ claims }: { claims: Patient360ClaimDto[] }) {
  const approved = claims.filter(c => ['APPROVED', 'PAID', 'COMPLETED'].includes(c.claimStatus?.toUpperCase() || ''));
  const pending = claims.filter(c => ['PENDING', 'REVIEW', 'SUBMITTED'].includes(c.claimStatus?.toUpperCase() || ''));
  const rejected = claims.filter(c => ['REJECTED', 'DENIED'].includes(c.claimStatus?.toUpperCase() || ''));
  const reimbursed = claims.filter(c => ['PAID', 'REIMBURSED'].includes(c.claimStatus?.toUpperCase() || ''));

  const stats = [
    { label: 'Approved', count: approved.length, amount: approved.reduce((s, c) => s + (c.claimedAmount || 0), 0), icon: CheckCircle, color: colorSystem.success.dark, iconBg: colorSystem.success.gradient },
    { label: 'Pending', count: pending.length, amount: pending.reduce((s, c) => s + (c.claimedAmount || 0), 0), icon: Clock, color: colorSystem.warning.dark, iconBg: colorSystem.warning.gradient },
    { label: 'Rejected', count: rejected.length, amount: rejected.reduce((s, c) => s + (c.claimedAmount || 0), 0), icon: XCircle, color: colorSystem.danger.dark, iconBg: colorSystem.danger.gradient },
    { label: 'Reimbursed', count: reimbursed.length, amount: reimbursed.reduce((s, c) => s + (c.claimedAmount || 0), 0), icon: DollarSign, color: colorSystem.primary.dark, iconBg: colorSystem.primary.gradient },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {stats.map(s => {
        const Icon = s.icon;
        return (
          <div key={s.label} className="flex items-center gap-2.5 rounded-lg border border-border/40 bg-white dark:bg-card px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
            <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: s.iconBg }}>
              <Icon className="w-3.5 h-3.5" style={{ color: s.color }} />
            </div>
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-foreground">{s.count} <span className="font-normal text-[11px] text-muted-foreground">· {formatCurrency(s.amount)}</span></p>
              <p className="text-[11px] text-muted-foreground">{s.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const FinancialsTab = memo(function FinancialsTab({
  financial,
  claims,
  summary,
}: FinancialsTabProps) {
  const hasLedger = financial.ledger && financial.ledger.length > 0;
  const hasClaims = claims.length > 0;
  const hasSchemes = summary.schemeBreakdown && summary.schemeBreakdown.length > 0;
  const hasData = hasLedger || hasClaims;

  // ── KPI items — same design token system as Cycle page ───────────
  const kpiItems: OpsKpiItem[] = useMemo(() => {
    const pendingClaims = claims.filter(c => ['PENDING', 'REVIEW', 'SUBMITTED'].includes(c.claimStatus?.toUpperCase() || ''));
    const pendingAmount = pendingClaims.reduce((sum, c) => sum + (c.claimedAmount || 0), 0);
    const totalCovered = financial?.utilization?.totalCovered ?? 0;
    const total = (summary.totalAssistance ?? 0) + (summary.remainingEligibility ?? 0);
    const usedPct = total > 0 ? Math.round(((summary.totalAssistance ?? 0) / total) * 100) : 0;

    return [
      {
        label: 'Total Assistance',
        value: formatCurrency(summary.totalAssistance ?? 0),
        sub: `${usedPct}% utilized`,
        icon: DollarSign,
        variant: 'blue' as const,
      },
      {
        label: 'Remaining Eligibility',
        value: formatCurrency(summary.remainingEligibility ?? 0),
        sub: `${100 - usedPct}% available`,
        icon: Wallet,
        variant: 'green' as const,
      },
      {
        label: 'Total Covered',
        value: formatCurrency(totalCovered),
        icon: TrendingUp,
        variant: 'purple' as const,
      },
      {
        label: 'Pending Claims',
        value: formatCurrency(pendingAmount),
        sub: `${pendingClaims.length} claim${pendingClaims.length !== 1 ? 's' : ''}`,
        icon: Clock,
        variant: 'orange' as const,
      },
      {
        label: 'Total Claims',
        value: String(claims.length),
        icon: ReceiptText,
        variant: 'red' as const,
      },
    ];
  }, [claims, financial, summary]);

  // Ledger table columns
  const ledgerColumns: DataGridColumn<NonNullable<Patient360FinancialDto['ledger']>[number]>[] = useMemo(() => [
    {
      key: 'createdAt',
      label: 'Date',
      render: (row) => (
        <span className="text-[11px]">{formatDisplayDate(String(row.createdAt ?? ''))}</span>
      ),
    },
    {
      key: 'transactionType',
      label: 'Type',
      render: (row) => <StatusBadge status={formatWorkflowAction(String(row.transactionType ?? 'Unknown'))} />,
    },
    {
      key: 'referenceType',
      label: 'Reference',
      render: (row) => (
        <span className="text-[11px] text-muted-foreground">{row.notes || row.referenceType || '—'}</span>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      render: (row) => (
        <span className="text-[11px] font-medium">{formatCurrency(Number(row.amount ?? 0))}</span>
      ),
    },
  ], []);

  // Claims table columns
  const claimColumns: DataGridColumn<Patient360ClaimDto>[] = useMemo(() => [
    {
      key: 'claimNumber',
      label: 'Claim #',
      render: (row) => <span className="text-[11px] font-medium">{row.claimNumber || '—'}</span>,
    },
    {
      key: 'caseNumber',
      label: 'Case',
      render: (row) => <span className="text-[11px]">{row.caseNumber || '—'}</span>,
    },
    {
      key: 'claimStatus',
      label: 'Status',
      render: (row) => (
        <StatusBadge
          status={row.claimStatus || 'Unknown'}
          variant={getClaimStatusVariant(row.claimStatus || '')}
        />
      ),
    },
    {
      key: 'submittedAt',
      label: 'Submitted',
      render: (row) => <span className="text-[11px]">{formatDisplayDate(String(row.submittedAt ?? ''))}</span>,
    },
    {
      key: 'claimedAmount',
      label: 'Amount',
      align: 'right',
      render: (row) => <span className="text-[11px] font-medium">{formatCurrency(Number(row.claimedAmount ?? 0))}</span>,
    },
  ], []);

  return (
    <div className="space-y-3">
      {/* KPI Summary Strip — same OpsKpiStrip used on Cycle page */}
      <OpsKpiStrip items={kpiItems} />

      {/* Claim Status Breakdown Strip */}
      {hasClaims && <ClaimStatusStrip claims={claims} />}

      {/* Main 2-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3">
        {/* LEFT — Transactions & Claims */}
        <div className="space-y-3 min-w-0">
          {/* Ledger / Transactions */}
          {hasLedger ? (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Assistance Ledger</p>
                <Button variant="ghost" size="sm" className="h-6 gap-1 text-[11px] text-muted-foreground">
                  <Download className="h-3 w-3" /> Export
                </Button>
              </div>
              <DataGrid
                columns={ledgerColumns}
                data={financial.ledger!}
                getRowKey={(row) => `${row.createdAt}-${row.amount}-${row.referenceType ?? 'ref'}`}
                emptyTitle="No ledger entries"
              />
            </div>
          ) : (
            <FinancialEmptyState />
          )}

          {/* Claims History */}
          {hasClaims && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Claims History</p>
                <span className="text-[11px] text-muted-foreground">{claims.length} total</span>
              </div>
              <DataGrid
                columns={claimColumns}
                data={claims}
                getRowKey={(row) => row.claimId}
                emptyTitle="No claims found"
              />
            </div>
          )}

          {/* If no data at all, show just the empty state */}
          {!hasData && !hasLedger && (
            <div className="rounded-xl border border-border/50 bg-[#F8FAFC] dark:bg-muted/10 p-4">
              <p className="text-[11px] text-muted-foreground text-center">
                Transaction history and claims will populate as financial activity occurs.
              </p>
            </div>
          )}
        </div>

        {/* RIGHT — Scheme Summary + Eligibility + Pending */}
        <div className="space-y-3">
          <EligibilityPanel summary={summary} />
          <SchemeSummaryPanel schemes={summary.schemeBreakdown} />
          <PendingActionsPanel claims={claims} />

          {/* Quick financial stats if no schemes */}
          {!hasSchemes && (
            <div className="rounded-xl border border-border/50 bg-[#F8FAFC] dark:bg-muted/10 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-3 w-3 text-muted-foreground" />
                <p className="text-[11px] font-semibold text-muted-foreground">Financial Summary</p>
              </div>
              <div className="space-y-1.5 text-[11px]">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Assistance received</span>
                  <span className="font-medium">{formatCurrency(summary.totalAssistance)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Remaining eligibility</span>
                  <span className="font-medium text-primary">{formatCurrency(summary.remainingEligibility)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Claims filed</span>
                  <span className="font-medium">{claims.length}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

